import {
  Activity,
  Award,
  Banknote,
  Calendar,
  CalendarClock,
  CalendarDays,
  CheckCircle2,
  CircleSlash,
  ClipboardPaste,
  Compass,
  Crown,
  DollarSign,
  Flame,
  Footprints,
  Layers,
  Library,
  LineChart,
  ListChecks,
  Lock,
  NotebookPen,
  Palette,
  PiggyBank,
  Receipt,
  Scale,
  ShieldCheck,
  Snowflake,
  Sparkles,
  Target,
  Telescope,
  TrendingUp,
  Trophy,
  Vault,
  Wand2,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { memo, useCallback, useMemo, useState } from 'react';
import {
  BottomSheet,
  Card,
  FullScreenModal,
  ProgressBar,
} from '../../components';
import { useActions, useAppData } from '../../data';
import {
  ACHIEVEMENTS,
  ACHIEVEMENT_CATEGORIES,
  TIER_COLOR,
  type AchievementCategory,
  type AchievementDef,
} from '../../lib/achievements/registry';
import { cn } from '../../lib/cn';
import { formatRelativeDate } from '../../lib/date';

type Filter = 'all' | AchievementCategory;

type Props = {
  open: boolean;
  onClose: () => void;
};

/**
 * Static map keeps the bundle slim — `import * as Lucide` would
 * disable tree-shaking and pull every icon (~140 KB).
 */
const ICONS: Record<string, LucideIcon> = {
  Activity,
  Award,
  Banknote,
  Calendar,
  CalendarClock,
  CalendarDays,
  CheckCircle2,
  CircleSlash,
  ClipboardPaste,
  Compass,
  Crown,
  DollarSign,
  Flame,
  Footprints,
  Layers,
  Library,
  LineChart,
  ListChecks,
  NotebookPen,
  Palette,
  PiggyBank,
  Receipt,
  Scale,
  ShieldCheck,
  Snowflake,
  Sparkles,
  Target,
  Telescope,
  TrendingUp,
  Trophy,
  Vault,
  Wand2,
  Zap,
};

function resolveLucide(name: string): LucideIcon {
  return ICONS[name] ?? Award;
}

/** Compact relative duration: 7d / 3w / 5mo / 2y. */
function compactDuration(iso: string, now: Date): string {
  const ms = now.getTime() - Date.parse(iso);
  if (!Number.isFinite(ms) || ms < 0) return '0d';
  const days = Math.floor(ms / (24 * 60 * 60 * 1000));
  if (days < 7) return `${days}d`;
  if (days < 30) return `${Math.floor(days / 7)}w`;
  if (days < 365) return `${Math.floor(days / 30)}mo`;
  return `${Math.floor(days / 365)}y`;
}

const FILTER_OPTIONS: { value: Filter; label: string }[] = [
  { value: 'all', label: 'All' },
  ...ACHIEVEMENT_CATEGORIES.map((c) => ({
    value: c.id as Filter,
    label: c.label,
  })),
];

/**
 * Settings → Achievements full sub-screen. Scrollable filter pills,
 * stat strip, and a 2-column card grid. Hidden achievements stay '???'
 * until unlocked.
 */
export function AchievementsScreen({ open, onClose }: Props) {
  const data = useAppData();
  const actions = useActions();
  const [filter, setFilter] = useState<Filter>('all');
  const [detailId, setDetailId] = useState<string | null>(null);
  const today = useMemo(() => new Date(), []);

  const ach = data.profile.achievements;
  const unlocks = ach?.unlocks ?? [];
  const unlockMap = useMemo(
    () => new Map(unlocks.map((u) => [u.id, u])),
    [unlocks],
  );

  const activeStreak = useMemo(() => {
    if (!ach) return 0;
    return Math.max(
      ach.streaks.daysLogged.current,
      ach.streaks.daysUnderPace.current,
      ach.streaks.noSpendDays.current,
      ach.streaks.pyfStreak.current,
    );
  }, [ach]);

  const visible = useMemo(() => {
    return ACHIEVEMENTS.filter((a) => {
      if (filter !== 'all' && a.category !== filter) return false;
      if (a.hidden && !unlockMap.has(a.id)) return false;
      return true;
    });
  }, [filter, unlockMap]);

  const progressMap = useMemo(() => {
    const m = new Map<string, { current: number; target: number } | null>();
    for (const def of visible) {
      m.set(def.id, progressFor(def, data));
    }
    return m;
  }, [visible, data]);

  const handleCardTap = useCallback((id: string) => setDetailId(id), []);

  const totalCount = ACHIEVEMENTS.length;
  const unlockedCount = unlocks.length;
  const detail = detailId
    ? ACHIEVEMENTS.find((a) => a.id === detailId) ?? null
    : null;

  return (
    <FullScreenModal open={open} onClose={onClose} title="Achievements">
      <div className="bg-bg pb-6">
        {/* Stat strip */}
        <div className="px-5 pt-4 grid grid-cols-3 gap-3">
          <Stat label="Unlocked" value={`${unlockedCount} / ${totalCount}`} icon={Trophy} />
          <Stat
            label="Streak"
            value={activeStreak > 0 ? `${activeStreak} days` : 'None'}
            valueDim={activeStreak === 0}
            icon={Flame}
          />
          <Stat
            label="Joined"
            value={ach?.installedAt ? compactDuration(ach.installedAt, today) : 'None'}
            valueDim={!ach?.installedAt}
            icon={Award}
          />
        </div>

        {/* Filter pills — horizontal scroll. touch-action:pan-x locks
            this row to horizontal gestures only, so a vertical scroll
            that briefly lands on a pill doesn't get interpreted as an
            ambiguous drag (which is what was making it jitter). */}
        <div
          className="flex flex-row gap-2 overflow-x-auto px-5 mt-4 pb-1"
          style={{
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            touchAction: 'pan-x',
            overscrollBehaviorX: 'contain',
          }}
        >
          {FILTER_OPTIONS.map((opt) => {
            const active = filter === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                onClick={() => setFilter(opt.value)}
                className={cn(
                  'shrink-0 rounded-full px-4 py-2 text-[13px] font-medium transition-colors',
                  active
                    ? 'bg-text-primary text-bg'
                    : 'bg-surface-secondary text-text-secondary',
                )}
              >
                {opt.label}
              </button>
            );
          })}
        </div>

        {/* Grid */}
        <div className="px-5 mt-4 grid grid-cols-2 gap-3">
          {visible.map((def) => (
            <AchievementCardView
              key={def.id}
              def={def}
              unlockedAt={unlockMap.get(def.id)?.unlockedAt ?? null}
              progress={progressMap.get(def.id) ?? null}
              today={today}
              onTap={handleCardTap}
            />
          ))}
        </div>

        {/* Toggle */}
        <label className="mt-6 mx-5 flex items-center justify-between gap-3 px-1 py-3 border-t border-surface-secondary">
          <span className="text-[14px] font-medium text-text-primary">
            Show achievement unlocks
          </span>
          <input
            type="checkbox"
            checked={ach?.achievementsEnabled !== false}
            onChange={(e) => actions.setAchievementsEnabled(e.target.checked)}
            className="w-5 h-5"
          />
        </label>
      </div>

      <BottomSheet
        open={detail !== null}
        onClose={() => setDetailId(null)}
        title={detail?.title ?? ''}
      >
        {detail ? (
          <DetailView
            def={detail}
            unlockedAt={unlockMap.get(detail.id)?.unlockedAt ?? null}
            progress={progressMap.get(detail.id) ?? progressFor(detail, data)}
            today={today}
          />
        ) : null}
      </BottomSheet>
    </FullScreenModal>
  );
}

function Stat({
  label,
  value,
  icon: Icon,
  valueDim = false,
}: {
  label: string;
  value: string;
  icon: LucideIcon;
  valueDim?: boolean;
}) {
  return (
    <Card elevation="sm" className="p-3 rounded-2xl">
      <Icon size={16} strokeWidth={1.75} className="text-text-tertiary" />
      <div
        className={cn(
          'mt-1 text-[17px] font-semibold tabular-nums truncate',
          valueDim ? 'text-text-tertiary' : 'text-text-primary',
        )}
      >
        {value}
      </div>
      <div className="text-[11px] font-medium uppercase tracking-[0.05em] text-text-tertiary">
        {label}
      </div>
    </Card>
  );
}

function progressFor(
  def: AchievementDef,
  data: ReturnType<typeof useAppData>,
): { current: number; target: number } | null {
  if (!def.targetValue) return null;
  const txCount = data.transactions.length;
  const streaks = data.profile.achievements?.streaks;
  switch (def.id) {
    case 'log_3':
    case 'log_25':
    case 'log_100':
    case 'log_500':
      return { current: txCount, target: def.targetValue };
    case 'streak_log_7':
    case 'streak_log_30':
    case 'streak_log_100':
      return { current: streaks?.daysLogged.current ?? 0, target: def.targetValue };
    case 'streak_under_pace_14':
      return { current: streaks?.daysUnderPace.current ?? 0, target: def.targetValue };
    case 'no_spend_3':
    case 'no_spend_7':
      return { current: streaks?.noSpendDays.current ?? 0, target: def.targetValue };
    case 'savings_100':
    case 'savings_1k':
    case 'savings_10k': {
      const total = data.goals.reduce((s, g) => s + (g.currentAmount || 0), 0);
      return { current: Math.round(total), target: def.targetValue };
    }
    default:
      return null;
  }
}

const AchievementCardView = memo(function AchievementCardView({
  def,
  unlockedAt,
  progress,
  today,
  onTap,
}: {
  def: AchievementDef;
  unlockedAt: string | null;
  progress: { current: number; target: number } | null;
  today: Date;
  onTap: (id: string) => void;
}) {
  const Icon = resolveLucide(def.icon);
  const unlocked = !!unlockedAt;
  const tierColor = TIER_COLOR[def.tier];
  return (
    <button
      type="button"
      onClick={() => onTap(def.id)}
      className="rounded-2xl p-4 bg-surface border border-surface-secondary text-left flex flex-col gap-2 min-h-[140px]"
    >
      <span
        className="w-10 h-10 rounded-full inline-flex items-center justify-center"
        style={{
          backgroundColor: unlocked
            ? tierColor
            : 'rgb(var(--surface-secondary))',
          filter: unlocked ? undefined : 'grayscale(1)',
          opacity: unlocked ? 1 : 0.6,
        }}
      >
        <Icon
          size={18}
          strokeWidth={1.75}
          className={unlocked ? 'text-white' : 'text-text-secondary'}
        />
      </span>
      <div
        className={cn(
          'text-[14px] font-semibold',
          unlocked ? 'text-text-primary' : 'text-text-secondary',
        )}
      >
        {def.title}
      </div>
      <div
        className={cn(
          'text-[12px] leading-snug',
          unlocked ? 'text-text-tertiary' : 'text-text-tertiary',
        )}
      >
        {def.description}
      </div>
      {!unlocked && progress ? (
        <div className="mt-1">
          <ProgressBar
            value={Math.min(1, progress.current / progress.target)}
          />
          <div className="mt-1 text-[10px] text-text-tertiary tabular-nums">
            {Math.min(progress.current, progress.target)} / {progress.target}
          </div>
        </div>
      ) : null}
      {unlocked && unlockedAt ? (
        <div className="text-[11px] text-text-tertiary">
          {formatRelativeDate(unlockedAt.slice(0, 10), today)}
        </div>
      ) : null}
    </button>
  );
});

function DetailView({
  def,
  unlockedAt,
  progress,
  today,
}: {
  def: AchievementDef;
  unlockedAt: string | null;
  progress: { current: number; target: number } | null;
  today: Date;
}) {
  const Icon = resolveLucide(def.icon);
  const unlocked = !!unlockedAt;
  return (
    <div className="px-screen-x py-4 flex flex-col items-center text-center gap-3">
      <span
        className="w-14 h-14 rounded-full inline-flex items-center justify-center"
        style={{
          backgroundColor: unlocked
            ? TIER_COLOR[def.tier]
            : 'rgb(var(--surface-secondary))',
        }}
      >
        {unlocked ? (
          <Icon size={26} strokeWidth={1.75} className="text-white" />
        ) : (
          <Lock size={22} strokeWidth={1.75} className="text-text-tertiary" />
        )}
      </span>
      <h3 className="text-[18px] font-semibold text-text-primary">
        {def.title}
      </h3>
      <p className="text-[14px] text-text-secondary max-w-[280px]">
        {def.description}
      </p>
      <p className="text-[12px] text-text-tertiary">{def.requirement}</p>
      {progress ? (
        <div className="w-full max-w-[280px] mt-2">
          <ProgressBar
            value={Math.min(1, progress.current / progress.target)}
          />
          <div className="mt-1 text-[12px] text-text-tertiary tabular-nums text-center">
            {Math.min(progress.current, progress.target)} / {progress.target}
          </div>
        </div>
      ) : null}
      {unlockedAt ? (
        <div className="mt-2 text-[12px] text-text-tertiary">
          Earned {formatRelativeDate(unlockedAt.slice(0, 10), today)}
        </div>
      ) : null}
    </div>
  );
}
